import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Input } from '../../components/ui/input';
import NumberInput from '../shared/NumberInput';
import PickSelect from '../shared/PickSelect';
import * as taxApi from './propertyTaxApi';

const NAVY = '#070B5F';
const LABEL = 'text-[11px] font-semibold uppercase tracking-wider text-slate-500';

export const NOT_SURE_WARNING = 'Tax may be understated. Check the appraisal district record before sending.';
export const PARTIAL_COPY = 'We matched the county but not every taxing unit. Pick the missing ones below.';
export const MISS_COPY = 'Address not matched. Using the county estimate.';
export const OUTSIDE_CITY_COPY = 'This address is outside city limits (US Census), so no city tax applies, even when the mailing address names a city.';
export const NO_SCHOOL_COPY = 'Pick a school district to use looked-up rates. Until then the county estimate applies.';

const fmtRate = (r) => (r == null ? 'rate not reported' : `${r} per $100`);
const fmt$ = (n) => (n == null ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }));

const unitOptions = (first, units) => [{ value: '', label: first }, ...units.map((u) => ({ value: u.id, label: u.name }))];

// Controlled by FeeWorksheetPage: `value` = {tax_unit_ids, homestead, tax_lookup_status,
// tax_special_answer}.
// The worksheet numbers always come from the server (`taxBasis`); nothing here
// computes a rate or a label.
export default function TaxLookupPanel({
  state, address, county, counties, value, onChange, onLookupApplied, onCountyChange,
  manualRate, onManualRateChange, taxBasis, stale, hideManualRate = false,
}) {
  const [busy, setBusy] = useState(false);
  const [units, setUnits] = useState(null);
  const [unitsCounty, setUnitsCounty] = useState('');
  const [search, setSearch] = useState('');
  const [unitsError, setUnitsError] = useState(false);
  const [outsideCity, setOutsideCity] = useState(''); // county of the last lookup that found no city
  const reqRef = useRef(0);

  // Fetch the option lists whenever the county changes (or a saved worksheet is restored).
  useEffect(() => {
    if (!county || unitsCounty === county) return undefined;
    const req = ++reqRef.current;
    setUnitsError(false);
    taxApi.getUnits(county).then((u) => {
      if (reqRef.current !== req) return;
      setUnits(u);
      setUnitsCounty(county);
    }).catch(() => {
      if (reqRef.current === req) { setUnits(null); setUnitsError(true); }
    });
    return undefined;
  }, [county, unitsCounty]);

  const status = value.tax_lookup_status;
  const special = value.tax_special_answer || null; // yes | no | unsure | null
  const ids = value.tax_unit_ids;
  const list = (k) => (units && units[k]) || [];
  const unitsReady = Boolean(units) && unitsCounty === county;
  const cityId = (ids || []).find((i) => list('cities').some((c) => c.id === i)) || '';
  const schoolId = (ids || []).find((i) => list('schools').some((c) => c.id === i)) || '';
  const specialIds = (ids || []).filter((i) => list('special').some((c) => c.id === i));

  if (state !== 'TX') return null;

  const replaceIds = (kind, newId) => {
    const kindIds = new Set(list(kind).map((u) => u.id));
    const next = (ids || []).filter((i) => !kindIds.has(i));
    if (newId) next.push(newId);
    onChange({ tax_unit_ids: next });
  };
  const toggleSpecial = (id) => {
    const next = specialIds.includes(id) ? (ids || []).filter((i) => i !== id) : [...(ids || []), id];
    onChange({ tax_unit_ids: next });
  };
  const chooseSpecial = (answer) => {
    const patch = { tax_special_answer: answer };
    if (answer !== 'yes' && specialIds.length) {
      const drop = new Set(specialIds);
      patch.tax_unit_ids = (ids || []).filter((i) => !drop.has(i));
    }
    onChange(patch);
  };

  const doLookup = async () => {
    setBusy(true);
    try {
      const r = await taxApi.lookup(address.trim());
      if (r.options) { setUnits(r.options); setUnitsCounty(r.county); } // before onLookupApplied: avoids a duplicate fetch
      setSearch('');
      setOutsideCity(r.status !== 'miss' && r.unincorporated && r.county ? r.county : '');
      const patch = { tax_lookup_status: r.status, tax_special_answer: null };
      if (r.status !== 'miss' && r.county) {
        patch.tax_unit_ids = r.unit_ids || [];
      } else {
        patch.tax_unit_ids = null;
      }
      onLookupApplied(patch, r.county || null);
    } catch (e) {
      setOutsideCity('');
      onLookupApplied({ tax_lookup_status: 'miss', tax_unit_ids: null, tax_special_answer: null }, null);
    } finally {
      setBusy(false);
    }
  };

  const filtered = list('special').filter((u) => u.name.toLowerCase().includes(search.trim().toLowerCase()));
  const unreportedPicked = list('special').filter((u) => specialIds.includes(u.id) && u.rate == null);
  const showSelects = Boolean(county) && (unitsReady || unitsError);
  const final = special === 'yes' || special === 'no' || special === 'unsure';
  const components = taxBasis?.components || [];
  // The server only uses looked-up rates once a school district is chosen.
  const lookupApplies = Boolean(schoolId) && manualRate == null;

  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-3" data-testid="tax-lookup-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Texas property tax</div>
        <button
          type="button"
          onClick={doLookup}
          disabled={busy || address.trim().length < 5}
          data-testid="tax-lookup-btn"
          className="inline-flex items-center gap-2 px-3 h-8 rounded-md text-white text-xs font-medium disabled:opacity-50"
          style={{ background: NAVY }}
        >
          {busy ? <Loader2 className="animate-spin" size={12} /> : <Search size={12} />} Look up taxes
        </button>
      </div>

      {status === 'partial' && <p className="text-[12px] text-amber-800" data-testid="tax-status-msg">{PARTIAL_COPY}</p>}
      {status === 'miss' && <p className="text-[12px] text-amber-800" data-testid="tax-status-msg">{MISS_COPY}</p>}

      <PickSelect
        label="County" value={county || ''} onChange={onCountyChange} testId="tax-county" placeholder="Select county…"
        options={counties.map((c) => ({ value: c, label: c }))}
      />

      {showSelects && unitsReady && (
        <>
          <PickSelect
            label="City" value={cityId} onChange={(v) => replaceIds('cities', v)} testId="tax-city"
            options={unitOptions('Unincorporated', list('cities'))}
          />
          {outsideCity === county && !cityId && (
            <p className="text-[12px] text-slate-500 -mt-2" data-testid="tax-outside-city">{OUTSIDE_CITY_COPY}</p>
          )}
          <PickSelect
            label="School district" value={schoolId} onChange={(v) => replaceIds('schools', v)} testId="tax-school"
            options={unitOptions('Select school district…', list('schools'))}
          />

          <div data-testid="tax-special-q">
            <div className={LABEL}>Is this property in a MUD, PID, or other special district? *</div>
            <div className="mt-1 flex gap-4 text-sm">
              {[['yes', 'Yes'], ['no', 'No'], ['unsure', 'Not sure']].map(([k, l]) => (
                <label key={k} className="inline-flex items-center gap-1">
                  <input type="radio" name="tax-special" value={k} checked={special === k} onChange={() => chooseSpecial(k)} data-testid={`tax-special-${k}`} />
                  {l}
                </label>
              ))}
            </div>
            {special === 'unsure' && <p className="text-[12px] text-amber-800 mt-1" data-testid="tax-special-warning">{NOT_SURE_WARNING}</p>}
            {special === 'yes' && (
              <div className="mt-2" data-testid="tax-special-select">
                <Input
                  type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search districts…"
                  data-testid="tax-special-search"
                  className="h-8"
                />
                <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-100 divide-y divide-slate-50">
                  {filtered.length === 0 && <div className="px-3 py-2 text-[12px] text-slate-400">No districts found.</div>}
                  {filtered.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50">
                      <input type="checkbox" checked={specialIds.includes(u.id)} onChange={() => toggleSpecial(u.id)} data-testid={`tax-special-opt-${u.id}`} />
                      <span className="flex-1">{u.name}</span>
                      <span className="text-[11px] text-slate-400">{fmtRate(u.rate)}</span>
                    </label>
                  ))}
                </div>
                {unreportedPicked.length > 0 && (
                  <p className="text-[12px] text-amber-800 mt-1" data-testid="tax-special-unreported">
                    Rate not reported for {unreportedPicked.map((u) => u.name).join(', ')}. Tax may be understated; verify or enter a manual rate.
                  </p>
                )}
              </div>
            )}
          </div>
        </>
      )}
      {ids != null && unitsReady && !schoolId && manualRate == null && (
        <p className="text-[12px] text-amber-800" data-testid="tax-no-school">{NO_SCHOOL_COPY}</p>
      )}
      {county && unitsError && <p className="text-[12px] text-slate-500">Could not load taxing units for this county. Using the county estimate.</p>}

      <label className={`flex items-center gap-2 text-sm ${lookupApplies ? '' : 'text-slate-400'}`}>
        <input type="checkbox" checked={Boolean(value.homestead)} disabled={!lookupApplies} onChange={(e) => onChange({ homestead: e.target.checked })} data-testid="tax-homestead" />
        Primary residence (homestead)
        {!lookupApplies && <span className="text-[11px]">(looked-up rates only)</span>}
      </label>

      {!hideManualRate && (
        <label className="block">
          <span className={LABEL}>Manual tax rate (overrides lookup)</span>
          <div className="mt-1 flex items-center rounded-md border border-slate-200 focus-within:ring-2 focus-within:ring-[#6BA82E]/40">
            <NumberInput
              value={manualRate} onChange={onManualRateChange} data-testid="tax-manual-rate"
              className="w-full h-9 px-3 text-sm border-0 shadow-none focus-visible:ring-0"
            />
            <span className="pr-3 text-slate-400 text-xs">%</span>
          </div>
        </label>
      )}

      {value.tax_unit_ids != null && !final && manualRate == null && (
        <p className="text-[12px] text-slate-500" data-testid="tax-result-pending">Answer the special-district question to finalize the tax rate.</p>
      )}
      {taxBasis && (final || manualRate != null || value.tax_unit_ids == null) && (
        <div className="rounded-md bg-slate-50 px-3 py-2" data-testid="tax-result">
          <div className="text-sm font-semibold" style={{ color: NAVY }} data-testid="tax-result-label">{taxBasis.label}</div>
          {taxBasis.annual_tax != null && <div className="text-[12px] text-slate-600">Annual tax about {fmt$(taxBasis.annual_tax)}</div>}
          {stale && <div className="text-[11px] text-slate-400">Inputs changed. Click Recalculate to update.</div>}
          {components.length > 0 && (
            <ul className="mt-1 text-[11px] text-slate-500" data-testid="tax-components">
              {components.map((c) => <li key={`${c.kind}-${c.id}`}>{c.name} ({c.kind}): {fmtRate(c.rate)}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
